/**
 * 「すべて見る」: 段階ごとの用途の一覧
 *
 * 深さ 3 の用途は各段階の［そのほか］の中に入れる（重要度が低いほどタップが増える）。
 * 今は使えない用途は灰色にして、使えない理由を添える。
 */

import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import AiMark, { AiMarkLegend } from '@/components/chat/AiMark';
import { CHAT_GROUP_ORDER } from '@/constants/chat';
import type { ChatFlowButton, ChatHomeResponse } from '@/services/chat';

const DEEP = 3;

function FlowRow({ flow, onStart }: { flow: ChatFlowButton; onStart: (flowId: string) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onStart(flow.flowId)}
        disabled={!flow.available}
        aria-describedby={flow.available ? undefined : `reason-${flow.flowId}`}
        className="flex w-full flex-col items-start rounded-lg px-2 py-2 text-left hover:bg-primary-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
      >
        <span className={`text-sm ${flow.available ? 'text-text-900' : 'text-text-400'}`}>
          {flow.label}
          {flow.usesModel ? (
            <span className="ml-1">
              <AiMark className={flow.available ? 'text-primary-500' : 'text-text-300'} />
            </span>
          ) : (
            <span className="ml-1.5 text-[10px] text-text-400">AIを使いません</span>
          )}
        </span>
        {flow.available ? (
          <span className="text-[11px] text-text-500">{flow.description}</span>
        ) : (
          <span id={`reason-${flow.flowId}`} className="text-[11px] text-text-400">
            {flow.disabledReason}
          </span>
        )}
      </button>
    </li>
  );
}

function Group({
  label,
  flows,
  onStart,
}: {
  label: string;
  flows: ChatFlowButton[];
  onStart: (flowId: string) => void;
}) {
  const [showMore, setShowMore] = useState(false);
  const main = flows.filter((flow) => flow.depth < DEEP);
  const more = flows.filter((flow) => flow.depth >= DEEP);
  return (
    <section className="space-y-1">
      <h3 className="px-2 text-[11px] font-medium tracking-wide text-text-500">{label}</h3>
      <ul className="divide-y divide-dashed divide-text-100">
        {main.map((flow) => (
          <FlowRow key={flow.flowId} flow={flow} onStart={onStart} />
        ))}
        {more.length > 0 && (
          <li>
            <button
              type="button"
              onClick={() => setShowMore((value) => !value)}
              aria-expanded={showMore}
              className="flex w-full items-center gap-1 px-2 py-2 text-left text-sm text-text-600 hover:bg-text-50"
            >
              そのほか
              <span className="text-[11px] text-text-400">
                {more.map((flow) => flow.label).join('・')}
              </span>
              <ChevronDownIcon
                className={`ml-auto h-4 w-4 transition-transform ${showMore ? 'rotate-180' : ''}`}
              />
            </button>
            {showMore && (
              <ul className="pl-3">
                {more.map((flow) => (
                  <FlowRow key={flow.flowId} flow={flow} onStart={onStart} />
                ))}
              </ul>
            )}
          </li>
        )}
      </ul>
    </section>
  );
}

export default function ChatAllFlows({
  home,
  onStart,
}: {
  home: ChatHomeResponse;
  onStart: (flowId: string) => void;
}) {
  const labels = new Map((home.groups ?? []).map((group) => [group.id, group.label]));
  return (
    <div className="space-y-4">
      <AiMarkLegend className="px-2" />
      {CHAT_GROUP_ORDER.map((groupId) => {
        const flows = (home.flows ?? []).filter((flow) => flow.group === groupId);
        if (flows.length === 0) return null;
        return (
          <Group
            key={groupId}
            label={labels.get(groupId) ?? groupId}
            flows={flows}
            onStart={onStart}
          />
        );
      })}
    </div>
  );
}
